/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { calendarApi, settingsApi } from '../../api';
import FormModal from '../../components/FormModal';
import { RowMenu, SettingGroup, SettingRow } from '../../components/SettingGroups';
import { IconHourglass, IconPlusCircle, IconSearch } from '../../components/icons';

import { Empty, money, useLoader } from '../../components/common';
import {
  CATALOG_FALLBACK,
  PRODUCT_IMAGES,
  SERVICE_IMAGES,
} from '../../catalogImages';
import ServiceDialog from './ServiceDialog';
import { SettingsPage, Panel } from './shell';
import { useAuth } from '../../AuthContext';

/* The สินค้า tab wears a bottle on the original and icons.js carries none.
   Kept here rather than in the shared set — no other screen asks for it. */
const IconBottle = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 2.5h4v3.2l1.7 2.5a4 4 0 0 1 .8 2.4v8.4a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-8.4a4 4 0 0 1 .8-2.4L10 5.7Z" />
    <path d="M7.5 13.5h9" />
  </svg>
);

const tabLabel = (Icon, text) => (
  <span className="inline-flex items-center gap-2"><Icon size={16} /> {text}</span>
);

/** Both catalogues carry an English and a Thai name and the page shows both. */
const fullName = (r) => [r.name_en, r.name_th].filter(Boolean).join(' ');

/**
 * Buckets rows into the category each one names, keeping the order the API
 * sent — that order is the shop's own sort_order, so the groups land where the
 * shop put them. `known` seeds the groups the shop has declared, so one created
 * from the สร้างบริการ menu is on the page before anything is put in it;
 * สินค้า has no such list and falls back to whatever the catalogue mentions.
 */
function byCategory(rows, nameOf, known = []) {
  const groups = [];
  const seen = new Map();

  const group = (id, name) => {
    if (!seen.has(id)) {
      const g = { id, name: name || 'ไม่ระบุกลุ่ม', items: [] };
      seen.set(id, g);
      groups.push(g);
    }
    return seen.get(id);
  };

  known.forEach((c) => group(c.id, c.name || c.name_th || c.name_en));
  rows.forEach((r) => group(r.category_id ?? null, nameOf(r)).items.push(r));

  return groups;
}

const serviceCategory = (s) => s.category_name || s.category_name_th || s.category_name_en;
const productCategory = (p) => p.category_name_th;

const Thumb = ({ src, fallback = CATALOG_FALLBACK }) => (
  <span className="w-[34px] h-[34px] flex-none rounded-sm bg-gray-100 overflow-hidden">
    <img
      src={src || fallback}
      alt=""
      className="w-full h-full object-cover"
      onError={(event) => {
        if (event.currentTarget.src.endsWith(fallback)) return;
        event.currentTarget.src = fallback;
      }}
    />
  </span>
);

/** The original stacks the label over the number instead of putting them side by side. */
const PriceCell = ({ label, value }) => (
  <span className="flex flex-col items-end">
    <span className="text-[12px] text-gray-400">{label}</span>
    <span className="text-[14px] font-semibold tabular-nums">{value}</span>
  </span>
);

export function SettingProduct() {
  const { branch, branches, branchId } = useAuth();
  const [tab, setTab] = useState('service');
  const [creating, setCreating] = useState(null);   // { kind, categoryId } while open
  const [editing, setEditing] = useState(null);     // { kind, row }
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const services = useLoader(() => settingsApi.services(), []);
  const products = useLoader(() => settingsApi.products(), []);
  // เตียง and เอเจนซี่ are the dialog's own two lists and neither belongs to
  // this page's catalogue, so they come from the shared lookup call.
  const lookups = useLoader(() => calendarApi.lookups(), []);

  const isProduct = tab === 'product';
  const q = search.trim().toLowerCase();
  const matches = (...parts) => !q || parts.filter(Boolean).join(' ').toLowerCase().includes(q);

  const allServiceCats = services.data?.categories || [];
  const serviceCats = allServiceCats.filter((c) => c.is_active);
  const productCats = (products.data?.categories || []).map((c) => ({
    value: c.id,
    label: c.name_th || c.name_en,
  }));
  const addonServices = (services.data?.services || []).filter(
    (s) => s.is_addon && s.is_active_at_branch !== false
  );
  const branchName = branch?.displayName || branch?.name_en || branch?.code || 'สาขานี้';

  /* The live dialog has one display name plus the Thai and English localized
     names. Each is capped at 20 characters, followed by the cashier price
     override switch. */
  const GROUP_FIELDS = [
    {
      name: 'name', label: 'ชื่อกลุ่มบริการ', required: true,
      maxLength: 20, showCount: true, wide: true,
    },
    {
      name: 'nameTh', label: 'ชื่อกลุ่มบริการ (ภาษาไทย)',
      maxLength: 20, showCount: true, wide: true,
    },
    {
      name: 'nameEn', label: 'ชื่อกลุ่มบริการ (ภาษาอังกฤษ)',
      maxLength: 20, showCount: true, wide: true,
    },
    {
      name: 'priceEditable',
      label: 'สามารถปรับราคาบริการก่อนชำระเงินได้',
      type: 'checkbox',
      hint: 'ไม่สามารถปรับได้ / สามารถปรับได้',
      wide: true,
    },
  ];

  const PRODUCT_FIELDS = [
    { name: 'nameEn', label: 'ชื่อสินค้า', required: true, wide: true },
    { name: 'categoryId', label: 'กลุ่มสินค้า', type: 'select',
      options: productCats, required: true, wide: true },
    { name: 'sellPrice', label: 'ราคา', type: 'number', required: true },
    { name: 'costPrice', label: 'ต้นทุนสินค้า', type: 'number' },
    { name: 'stockAdd', label: 'เพิ่มจำนวนสินค้า', type: 'number', min: 0 },
    { name: 'stockSubtract', label: 'ลดจำนวนสินค้า', type: 'number', min: 0 },
    { name: 'barcode', label: 'เพิ่มบาร์โค้ด', wide: true },
    {
      name: 'inventory', label: 'จำนวนสินค้าคงเหลือ :', type: 'display', wide: true,
      render: (v) => (
        Number(v.currentStock || 0) +
        Number(v.stockAdd || 0) -
        Number(v.stockSubtract || 0)
      ),
    },
    {
      name: 'isActive', label: 'สถานะ', type: 'checkbox',
      hint: 'ไม่ใช้งาน/ใช้งาน', wide: true,
    },
    {
      name: 'priceEditable',
      label: 'สามารถปรับราคาบริการก่อนชำระเงินได้',
      type: 'checkbox',
      hint: 'ไม่สามารถปรับได้ / สามารถปรับได้',
      wide: true,
    },
  ];

  const submitProduct = async (v) => {
    await settingsApi.createProduct({
      nameEn: v.nameEn,
      categoryId: Number(v.categoryId) || null,
      costPrice: Number(v.costPrice) || 0, sellPrice: Number(v.sellPrice) || 0,
      barcode: v.barcode || null,
      stockAdd: Number(v.stockAdd) || 0,
      stockSubtract: Number(v.stockSubtract) || 0,
      isActive: !!v.isActive,
      priceEditable: !!v.priceEditable,
    });
    products.reload();
    setCreating(null);
  };

  /* Catalogue records are never presented as deleted: they move between active
     and inactive so old bills and bookings always keep their original labels. */
  const retireItem = (entity, row, loader) => (row.is_active === false
    ? { label: 'เปิดใช้งาน',
        onClick: async () => { await settingsApi.restore(entity, row.id); loader.reload(); } }
    : { label: 'ปิดใช้งาน', danger: true,
        onClick: async () => { await settingsApi.remove(entity, row.id); loader.reload(); } });

  const toggleServiceForBranch = (row) => (row.is_active_at_branch === false
    ? {
      label: 'เปิดใช้งาน',
      onClick: async () => {
        await settingsApi.update('services', row.id, { branchId, isActive: true });
        services.reload();
      },
    }
    : {
      label: 'ปิดใช้งาน',
      danger: true,
      onClick: async () => {
        await settingsApi.update('services', row.id, { branchId, isActive: false });
        services.reload();
      },
    });

  const groupMenu = (g) => {
    /* ไม่ระบุกลุ่ม is where the ungrouped collect rather than a row anyone can
       edit, and a group that has been retired still shows the services left in
       it — neither is a กลุ่มบริการ the menu can act on. */
    const category = isProduct ? null : allServiceCats.find((c) => c.id === g.id);

    return (
      <RowMenu
        items={[
          {
            label: isProduct ? 'เพิ่มสินค้าในกลุ่มนี้' : 'เพิ่มบริการในกลุ่มนี้',
            onClick: () => setCreating({
              kind: isProduct ? 'product' : 'service', categoryId: g.id ?? '',
            }),
          },
          category && {
            label: 'แก้ไขกลุ่ม',
            onClick: () => setEditing({ kind: 'group', row: category }),
          },
          category && retireItem('service-categories', category, services),
        ]}
      />
    );
  };

  const serviceRow = (s) => (
    <SettingRow
      key={s.id}
      label={(
        <span className="flex items-center gap-3">
          <Thumb src={s.image_url || SERVICE_IMAGES[s.code]} />
          <span>{fullName(s)}</span>
          <span className={`badge ${s.is_available_at_branch ? 'badge-green' : 'badge-grey'}`}>
            {s.is_available_at_branch ? `แสดงบนเว็บไซต์ ${branchName}` : `ไม่แสดงบนเว็บไซต์ ${branchName}`}
          </span>
          <span className={`badge ${s.is_active_at_branch !== false ? 'badge-green' : 'badge-grey'}`}>
            {s.is_active_at_branch !== false ? 'ใช้งาน' : 'ปิดใช้งาน'}
          </span>
        </span>
      )}
      trailing={(
        <span className="flex items-center gap-6 mr-2">
          {/* A duration the shop drops is retired rather than deleted so its
              sales keep their meaning — but it is no longer on the menu. */}
          {s.variants.filter((v) => v.isActive !== false).map((v) => (
            <PriceCell key={v.id} label={`${v.durationMinutes} นาที`} value={money(v.price)} />
          ))}
        </span>
      )}
      menu={(
        <RowMenu
          items={[
            { label: 'แก้ไข', onClick: () => setEditing({ kind: 'service', row: s }) },
            toggleServiceForBranch(s),
          ]}
        />
      )}
    />
  );

  const productRow = (p) => (
    <SettingRow
      key={p.id}
      label={(
        <span className="flex items-center gap-3">
          <Thumb src={p.image_url || PRODUCT_IMAGES[p.sku]} />
          <span className="flex flex-col">
            <span>{fullName(p)}</span>
            <span className="text-[12px] font-normal text-gray-400">{p.sku}</span>
          </span>
          <span className={`badge ${p.is_active !== false ? 'badge-green' : 'badge-grey'}`}>
            {p.is_active !== false ? 'ใช้งาน' : 'ปิดใช้งาน'}
          </span>
        </span>
      )}
      trailing={(
        <span className="flex items-center gap-6 mr-2">
          <PriceCell label="ต้นทุน" value={money(p.cost_price)} />
          <PriceCell label="ค่ามือ" value={money(p.commission)} />
          <PriceCell label="ราคาขาย" value={money(p.sell_price)} />
          <PriceCell label="คงเหลือ" value={`${Number(p.qty_on_hand)} ${p.unit || ''}`} />
        </span>
      )}
      menu={(
        <RowMenu
          items={[
            { label: 'แก้ไข', onClick: () => setEditing({ kind: 'product', row: p }) },
            retireItem('products', p, products),
          ]}
        />
      )}
    />
  );

  return (
    <SettingsPage
      tab={tab}
      onTab={setTab}
      action={isProduct ? 'สร้างสินค้า' : 'สร้างบริการ'}
      onAction={() => setCreating({ kind: isProduct ? 'product' : 'service' })}
      actionMenu={isProduct
        ? [
          { label: 'สินค้า', onClick: () => setCreating({ kind: 'product' }) },
          { label: 'กลุ่มสินค้า', onClick: () => setCreating({ kind: 'product-group' }) },
        ]
        : [
          { label: 'บริการ', onClick: () => setCreating({ kind: 'service' }) },
          { label: 'กลุ่มบริการ', onClick: () => setCreating({ kind: 'group' }) },
        ]}
      tabs={[
        { key: 'service', label: tabLabel(IconHourglass, 'บริการ') },
        { key: 'addon', label: tabLabel(IconPlusCircle, 'บริการเสริม') },
        { key: 'product', label: tabLabel(IconBottle, 'สินค้า') },
      ]}
      search={(
        <label className="search-field">
          <IconSearch size={17} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isProduct ? 'ค้นหาสินค้า' : 'ค้นหาบริการ'}
          />
        </label>
      )}
      showInactive={showInactive}
      onShowInactive={setShowInactive}
    >
      <div className="mb-2 ml-1 text-sm font-medium text-gray-500">
        {isProduct ? 'กลุ่มสินค้า' : 'กลุ่มบริการ'}
      </div>

      {isProduct ? (
        <Panel loader={products}>
          {(d) => {
            const rows = d.products
              .filter((p) => showInactive || p.is_active !== false)
              .filter((p) => matches(p.name_en, p.name_th, p.sku));
            const groups = byCategory(rows, productCategory);
            if (!groups.length) return <div className="card"><Empty icon="product" /></div>;

            return groups.map((g) => (
              <SettingGroup
                key={g.id ?? 'none'}
                title={g.name}
                count={g.items.length}
                countUnit="สินค้า"
                menu={groupMenu(g)}
              >
                {g.items.map(productRow)}
              </SettingGroup>
            ));
          }}
        </Panel>
      ) : (
        <Panel loader={services}>
          {(d) => {
            // บริการ lists real treatments; บริการเสริม the add-ons.
            const rows = d.services
              .filter((s) => (tab === 'addon' ? s.is_addon : !s.is_addon))
              .filter((s) => showInactive || s.is_active_at_branch !== false)
              .filter((s) => matches(s.name_en, s.name_th, s.code));
            const groups = byCategory(rows, serviceCategory, showInactive ? allServiceCats : serviceCats);
            if (!groups.length) return <div className="card"><Empty icon="product" /></div>;

            return groups.map((g) => (
              <SettingGroup
                key={g.id ?? 'none'}
                title={(() => {
                  const category = allServiceCats.find((item) => item.id === g.id);
                  return category?.is_active === false ? (
                    <span className="room-title-with-status">
                      <span>{g.name}</span>
                      <span className="badge badge-grey">ปิดใช้งาน</span>
                    </span>
                  ) : g.name;
                })()}
                count={g.items.length}
                countUnit="บริการ"
                menu={groupMenu(g)}
              >
                {g.items.map(serviceRow)}
              </SettingGroup>
            ));
          }}
        </Panel>
      )}

      {creating?.kind === 'product' && (
        <FormModal
          title="สร้างสินค้า"
          fields={PRODUCT_FIELDS}
          initial={{
            categoryId: creating.categoryId ?? '',
            sellPrice: 0,
            costPrice: 0,
            stockAdd: 0,
            stockSubtract: 0,
            currentStock: 0,
            isActive: true,
            priceEditable: false,
          }}
          onSubmit={submitProduct}
          onClose={() => setCreating(null)}
        />
      )}

      {creating?.kind === 'product-group' && (
        <FormModal
          title="สร้างกลุ่มสินค้า"
          fields={[
            {
              name: 'name', label: 'ชื่อกลุ่มสินค้า', required: true,
              maxLength: 10, showCount: true, wide: true,
            },
            {
              name: 'priceEditable',
              label: 'สามารถปรับราคาบริการก่อนชำระเงินได้',
              type: 'checkbox',
              hint: 'ไม่สามารถปรับได้ / สามารถปรับได้',
              wide: true,
            },
          ]}
          initial={{ priceEditable: false }}
          onSubmit={async (v) => {
            await settingsApi.createProductCategory({
              name: v.name,
              priceEditable: !!v.priceEditable,
            });
            products.reload();
            setCreating(null);
          }}
          onClose={() => setCreating(null)}
        />
      )}

      {(creating?.kind === 'service' || editing?.kind === 'service') && (
        <ServiceDialog
          service={editing?.row}
          // Which of the two service tabs the dialog was opened from is what
          // says whether it is a treatment or an add-on — their dialog carries
          // no control for it.
          isAddon={editing ? editing.row.is_addon : tab === 'addon'}
          categoryId={creating?.categoryId}
          categories={serviceCats}
          bedTypes={lookups.data?.bedTypes || []}
          agencies={lookups.data?.agencies || []}
          addonServices={addonServices.filter((a) => a.id !== editing?.row.id)}
          branches={branches}
          branchId={branchId}
          onLoadBranch={async (nextBranchId) => {
            if (!editing?.row?.id) return null;
            const data = await settingsApi.services(nextBranchId);
            return (data.services || []).find(
              (service) => String(service.id) === String(editing.row.id)
            ) || null;
          }}
          onSubmit={async (payload) => {
            if (editing) await settingsApi.update('services', editing.row.id, payload);
            else await settingsApi.createService(payload);
            services.reload();
            setCreating(null);
            setEditing(null);
          }}
          onClose={() => { setCreating(null); setEditing(null); }}
        />
      )}

      {(creating?.kind === 'group' || editing?.kind === 'group') && (
        <FormModal
          title={editing ? 'แก้ไขกลุ่มบริการ' : 'สร้างกลุ่มบริการ'}
          fields={GROUP_FIELDS}
          initial={{
            name: editing?.row.name || editing?.row.name_th || editing?.row.name_en || '',
            nameTh: editing?.row.name_th || '',
            nameEn: editing?.row.name_en || '',
            priceEditable: !!editing?.row.price_editable,
          }}
          onSubmit={async (v) => {
            const payload = {
              name: v.name,
              nameTh: v.nameTh || null,
              nameEn: v.nameEn || null,
              priceEditable: !!v.priceEditable,
            };
            if (editing) await settingsApi.update('service-categories', editing.row.id, payload);
            else await settingsApi.createServiceCategory(payload);
            services.reload();
            setCreating(null);
            setEditing(null);
          }}
          onClose={() => { setCreating(null); setEditing(null); }}
        />
      )}

      {editing?.kind === 'product' && (
        <FormModal
          title={`แก้ไขสินค้า — ${editing.row.name_th || editing.row.name_en}`}
          fields={PRODUCT_FIELDS}
          initial={{
            nameEn: editing.row.name_en,
            categoryId: editing.row.category_id ?? '',
            costPrice: editing.row.cost_price ?? 0,
            sellPrice: editing.row.sell_price ?? 0,
            stockAdd: 0,
            stockSubtract: 0,
            currentStock: Number(editing.row.qty_on_hand || 0),
            barcode: editing.row.barcode || '',
            isActive: editing.row.is_active !== false,
            priceEditable: !!editing.row.price_editable,
          }}
          onSubmit={async (v) => {
            await settingsApi.update('products', editing.row.id, {
              nameEn: v.nameEn,
              categoryId: Number(v.categoryId) || null,
              costPrice: v.costPrice === '' ? null : Number(v.costPrice),
              sellPrice: Number(v.sellPrice) || 0,
              barcode: v.barcode || null,
              isActive: !!v.isActive,
              priceEditable: !!v.priceEditable,
            });
            const stockChange = Number(v.stockAdd || 0) - Number(v.stockSubtract || 0);
            if (stockChange) {
              await settingsApi.adjustProductStock(editing.row.id, {
                quantity: stockChange,
                moveType: 'adjustment',
              });
            }
            products.reload();
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </SettingsPage>
  );
}

/* ── โปรโมชั่น ────────────────────────────────────────────────────────── */
